import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { Mail, ScanLine, X } from "lucide-react"
import { useState } from "react"
import {
  inquiryStateByFramework,
  useDeclarationInquiries,
} from "../../../../../api/declarationInquiriesApi"
import {
  useCreateQuestion,
  useDeclarationQuestions,
  useDeleteQuestion,
  useUpdateQuestion,
} from "../../../../../api/declarationQuestionsApi"
import {
  INQUIRY_STATUS_DISPLAY,
  inquiryStatusLabel,
  type InquiryStatus,
} from "../../../../../api/supplierInquiriesApi"
import { formatDate } from "../../../../../utils/date"
import { draftFor } from "../inquiry/inquiryDraft"
import type { FrameworkEditionInfo } from "../mapping/editionInfo"
import type { Geltung } from "../mapping/geltungMapping"
import type { StatementCard } from "../useStudioV2State"
import { AskBoxPlaceholder } from "./AskBoxPlaceholder"

/** SES-reported dead addresses — no reminder loop or reply can ever land, so these
 *  get a visible warning + guidance instead of the normal "waiting" framing.
 *  Mirrors ApproveGatePanel.tsx's own DEAD_ADDRESS_STATUSES (kept as a separate
 *  const rather than a shared import: the two components read it off differently
 *  shaped rows, and duplicating a 2-item Set costs less than a cross-panel dep). */
const DEAD_ADDRESS_STATUSES = new Set(["undeliverable", "complained"])

export interface AskBoxProps {
  declId: string
  framework: string
  statementId: string
  /** The statement's own extracted scope wording: the collector's prefilled
   *  question text. */
  scopeVerbatim: string | null
  /** The declaration's whole resolved pool — the "Bezugs-Kandidaten" line
   *  (kept to the pool Studio V2 already holds, no new query). */
  poolNumbers: string[]
  /** The full card, its DISPLAYED Geltung, the active Verordnung's display
   *  name and edition catalogue: everything `draftFor` (TUR-1211) needs to
   *  prefill the collector from the statement's own Rückfrage paragraph
   *  instead of the plain generic default below. `ArticleRow` (the sole
   *  mount site, only ever rendered while `inquiryNeeded` is true) already
   *  holds all four, so this never triggers a fetch of its own. */
  card: StatementCard
  geltung: Geltung
  frameworkName: string
  editionInfo: FrameworkEditionInfo | null
}

/** The plain, statement-agnostic fallback prefill: a scope quote (when the
 *  statement has one) plus the pool's candidate article numbers. Exported so
 *  a caller/test can assert an AskBox render against this exact text rather
 *  than a re-typed copy of it. */
export function defaultMessage(
  scopeVerbatim: string | null,
  poolNumbers: string[],
  translate: (
    key: TranslationKeys,
    v?: Record<string, string | number>,
  ) => string,
): string {
  const lines = [translate("MC_DECL_V2_ASKBOX_DRAFT_INTRO")]
  if (scopeVerbatim) {
    lines.push(
      translate("MC_DECL_V2_ASKBOX_DRAFT_QUOTE", { quote: scopeVerbatim }),
    )
  }
  lines.push(
    poolNumbers.length > 0
      ? translate("MC_DECL_V2_ASKBOX_DRAFT_CANDIDATES", {
          list: poolNumbers.join(", "),
        })
      : translate("MC_DECL_V2_ASKBOX_DRAFT_NO_CANDIDATES"),
  )
  return lines.join("\n\n")
}

/**
 * The live Rückfrage draft/sent/answered surface (D2), gated end-to-end on
 * `MC_DECLARATION_INQUIRY`: flag off renders `AskBoxPlaceholder` verbatim —
 * the journey never depends on the flag to stay unblocked, Anfordern remains
 * the always-on alternative either way.
 *
 * W5-1: the old per-framework IMMEDIATE send (a draft textarea + "Rückfrage
 * senden" behind a confirm dialog, one inquiry per framework) is gone. Every
 * "ask" card now only COLLECTS free-text questions via
 * `declarationQuestionsApi`: sending happens once, for every collected
 * question across every card, from the footer's request modal
 * (`RequestQuestionsDialog`, `useSendQuestions`). AskBox itself never sends
 * again. The sent/answered rendering below stays for inquiries the OLD flow
 * already created (still real rows in `useDeclarationInquiries`); the old
 * flow's SEND_FAILED retry panel went with it (F5, wave 5): nothing here
 * could ever set that state once its own send button was gone.
 */
export function AskBox({
  declId,
  framework,
  statementId,
  scopeVerbatim,
  poolNumbers,
  card,
  geltung,
  frameworkName,
  editionInfo,
}: AskBoxProps) {
  const { translate } = useTranslation()
  const inquiryFlag = useFeatureFlag("MC_DECLARATION_INQUIRY")
  // Gated on the same flag as `useStudioV2State`'s own instance: react-query
  // dedupes by query key, but any ENABLED observer is enough to fire the
  // fetch, so this call site has to gate itself too rather than relying on
  // the other one already being off.
  // Read unconditionally: the flag decides whether one may RAISE a Rückfrage, not
  // whether an already-sent one is visible (see useStudioV2State's note).
  const { data: inquiries } = useDeclarationInquiries(declId)

  // The question collector (W5-1): free-text items gathered on this card,
  // sent together as ONE declaration-scoped inquiry from the footer's
  // request modal: the only send path left, independent of this card.
  const { data: allDrafts } = useDeclarationQuestions(declId, false)
  const { data: allSent } = useDeclarationQuestions(declId, true)
  const createQuestion = useCreateQuestion(declId)
  const updateQuestion = useUpdateQuestion(declId)
  const deleteQuestion = useDeleteQuestion(declId)
  const cardDrafts = (allDrafts ?? []).filter(
    (q) => q.framework === framework && q.statementId === statementId,
  )
  const cardSent = (allSent ?? []).filter(
    (q) => q.framework === framework && q.statementId === statementId,
  )
  // A sent item only carries `inquiryId`, never its own reference: resolve
  // it off the same per-framework rows this card already reads for the
  // sent/answered pill above, rather than adding a second field to
  // `DeclarationQuestion` for one display line.
  const referenceByInquiry = new Map(
    (inquiries ?? []).map((row) => [row.inquiryId, row.reference]),
  )

  // TUR-1211: the collector starts from the statement's OWN Rückfrage
  // paragraph (`draftFor`) when it has one to ask, the same text `ArticleRow`
  // would show the reviewer via a request-mail composer, so the collector
  // never starts the reviewer from a blanker prompt than the statement's own
  // facts justify. `draftFor` returns "" for a card that asks nothing (never
  // reachable here in practice: `ArticleRow` mounts AskBox only while
  // `inquiryNeeded` holds), but the generic scope-quote/candidates prompt
  // below is the honest fallback either way, once only, on mount.
  const [newQuestion, setNewQuestion] = useState(() => {
    const drafted = draftFor(
      card,
      geltung,
      frameworkName,
      editionInfo,
      translate,
    )
    return drafted || defaultMessage(scopeVerbatim, poolNumbers, translate)
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState("")

  const renderCollectorBody = () => (
    <>
      <div className="sv2-ahd">
        <ScanLine className="sv2-ic" />
        {translate("MC_DECL_V2_ASKBOX_COLLECT_TITLE")}
      </div>
      {/* TUR-1652: nothing in master data can match yet, so no supplier
          answer alone resolves the scope. Same note the ARTIKEL slot shows.
          Quiet while no supplier is matched: the pool is empty for that
          reason, and the unmatched-supplier banner names that step. */}
      {card.supplierHasArticles === false && card.supplierMatched !== false && (
        <div data-testid="askbox-no-articles" className="sv2-offnote">
          <X className="sv2-ic" />
          {translate("MC_DECL_V2_ASKBOX_NO_ARTICLES_HINT")}
        </div>
      )}
      <div className="sv2-adraft">
        <textarea
          data-testid="askbox-collector-input"
          rows={3}
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          aria-label={translate("MC_DECL_V2_ASKBOX_QUESTION_PLACEHOLDER")}
          className="sv2-askta"
        />
      </div>
      <div className="sv2-askrow">
        <button
          type="button"
          data-testid="askbox-collector-add"
          disabled={newQuestion.trim().length === 0 || createQuestion.isPending}
          onClick={() => {
            createQuestion.mutate(
              {
                framework,
                statementId,
                question: newQuestion.trim(),
              },
              // Clear only once the POST lands: a rejected save (the hook
              // toasts it) must never discard what the reviewer typed.
              { onSuccess: () => setNewQuestion("") },
            )
          }}
          className="sv2-act sv2-primary"
        >
          {translate("MC_DECL_V2_ASKBOX_ADD")}
        </button>
        <span className="sv2-askn">
          {translate("MC_DECL_V2_ASKBOX_COLLECT_HINT")}
        </span>
      </div>
      {(cardDrafts.length > 0 || cardSent.length > 0) && (
        <ul className="mt-2 space-y-1.5">
          {cardDrafts.map((q) =>
            editingId === q.id ? (
              <li
                key={q.id}
                data-testid="askbox-collector-item-editing"
                className="flex items-center gap-2 text-[12px]"
              >
                <input
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  aria-label={translate(
                    "MC_DECL_V2_ASKBOX_QUESTION_EDIT_LABEL",
                  )}
                  className="min-w-0 flex-1 rounded border border-grey4 bg-transparent px-1.5 py-0.5 text-white focus:border-attention focus:outline-hidden focus:ring-2 focus:ring-attention/70"
                />
                <button
                  type="button"
                  className="shrink-0 text-[11px] text-grey5 hover:text-white"
                  // Same rule as the collector's own Add: the backend rejects
                  // an empty question, so a blank edit is never sent.
                  disabled={editingText.trim().length === 0}
                  onClick={() => {
                    updateQuestion.mutate(
                      {
                        questionId: q.id,
                        question: editingText.trim(),
                      },
                      // Clear editingId only once the PATCH actually lands:
                      // a rejected mutate must never look saved, so the
                      // field (and the typed text) stays put on error
                      // instead of reverting to the stale read-only row.
                      { onSuccess: () => setEditingId(null) },
                    )
                  }}
                >
                  {translate("MC_DECL_V2_ASKBOX_ADD_CONFIRM")}
                </button>
              </li>
            ) : (
              <li
                key={q.id}
                data-testid="askbox-collector-item"
                className="flex items-start gap-2 text-[12px] text-white"
              >
                <span className="min-w-0 flex-1">{q.question}</span>
                <button
                  type="button"
                  className="shrink-0 text-[11px] text-grey5 hover:text-white"
                  onClick={() => {
                    setEditingId(q.id)
                    setEditingText(q.question)
                  }}
                >
                  {translate("MC_DECL_V2_ASKBOX_QUESTION_EDIT")}
                </button>
                <button
                  type="button"
                  data-testid="askbox-collector-remove"
                  className="shrink-0 text-[11px] text-grey5 hover:text-white"
                  onClick={() => deleteQuestion.mutate(q.id)}
                >
                  {translate("MC_DECL_V2_ASKBOX_QUESTION_REMOVE")}
                </button>
              </li>
            ),
          )}
          {cardSent.map((q) => (
            <li
              key={q.id}
              data-testid="askbox-collector-sent-item"
              className="flex items-start gap-2 text-[12px] text-grey5"
            >
              <span className="min-w-0 flex-1">{q.question}</span>
              <span className="shrink-0 text-[11px]">
                {translate("MC_DECL_V2_REQUEST_SENT", {
                  reference:
                    (q.inquiryId && referenceByInquiry.get(q.inquiryId)) || "",
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  )

  if (!inquiryFlag) {
    return <AskBoxPlaceholder scopeVerbatim={scopeVerbatim} />
  }

  const row = inquiryStateByFramework(inquiries ?? []).get(framework) ?? null

  if (row?.state === "answered") {
    return (
      <>
        <p data-testid="askbox-answered" className="sv2-asksent mt-2">
          {translate("MC_DECL_V2_ASKBOX_ANSWERED", {
            date: formatDate(row.answeredAt) || "—",
          })}
        </p>
        <div data-testid="askbox-collector" className="mt-2">
          {renderCollectorBody()}
        </div>
      </>
    )
  }

  if (row?.state === "sent") {
    const messageText = row.message
    const statusDisplay: { labelKey: TranslationKeys } | undefined =
      INQUIRY_STATUS_DISPLAY[row.status as InquiryStatus]
    const isDeadAddress = DEAD_ADDRESS_STATUSES.has(row.status)
    // B8: the specific status, visible — not tooltip-only. "waiting" is the
    // plain, expected resting state the sent pill's own copy already names
    // ("wartet auf Antwort"), so it earns no second badge; anything BEYOND
    // that (reminded/escalated/ureview/undeliverable/complained) is new
    // information the reviewer cannot see any other way on this surface.
    const showStatusPill = statusDisplay && row.status !== "waiting"
    const statusLabel = inquiryStatusLabel(row.status, translate)
    return (
      <div data-testid="askbox-sent" className="mt-2">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="sv2-askstat">
                <Mail className="sv2-ic" />
                {translate("MC_DECL_V2_ASKBOX_SENT_PILL", {
                  date: formatDate(row.sentAt) || "—",
                })}
                {showStatusPill && (
                  <span
                    data-testid="askbox-status-pill"
                    className="sv2-tpill sv2-warn ml-1.5"
                  >
                    {translate(statusDisplay.labelKey)}
                  </span>
                )}
              </p>
            </TooltipTrigger>
            <TooltipContent side="top">{statusLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {isDeadAddress && (
          <p
            data-testid="askbox-dead-address"
            className="sv2-asksent text-[11px] text-destructive"
          >
            {translate("MC_DECL_V2_ASKBOX_DEAD_ADDRESS")}
          </p>
        )}
        {messageText && (
          <p className="sv2-asksent whitespace-pre-wrap">{messageText}</p>
        )}
        <div data-testid="askbox-collector" className="mt-2">
          {renderCollectorBody()}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="askbox-draft" className="sv2-askbox">
      {renderCollectorBody()}
    </div>
  )
}
